import {
    CardCollection,
    DateIO,
    format,
    Instance,
    Knot,
    Objekt,
    Promize,
    Query,
} from '@siposdani87/sui-js';
import { app } from '../app';
import { AssetService } from '@services/assetService';
import { LanguageService } from '@services/languageService';
import { NoteService } from '@services/noteService';
import { OrganizationService } from '@services/organizationService';
import { UtilService } from '@services/utilService';
import { initCardCollection } from '@utils/collectionSetup';
import { DashboardController } from './dashboardController';

export class NotesIndexController extends DashboardController {
    static readonly inject = [
        'instances',
        'noteService',
        'organizationService',
        'utilService',
        'languageService',
        'assetService',
    ] as const;

    noteCardCollection!: CardCollection;

    constructor(
        instances: Instance,
        private noteService: NoteService,
        private organizationService: OrganizationService,
        private utilService: UtilService,
        private languageService: LanguageService,
        private assetService: AssetService,
    ) {
        super(instances);
    }

    protected override _initLayout(): void {
        this._initToolbarButtons();
        this._initFilterForm();

        this._drawContent();
    }

    protected override _drawContent(): Promize | boolean {
        this._initNoteCardCollection();
        return true;
    }

    private _initToolbarButtons(): void {
        this.helper.fabButton('.btn-notes-new', this.dom, () => {
            this.noteService
                .newDialog('organizations', 'organization_id')
                .then(() => {
                    this._drawContent();
                });
        });

        this.helper.iconButton('.btn-site-home', this.dom, () => {
            this.state.goBack('site.home');
        });
    }

    private _initNoteCardCollection(): void {
        this.noteCardCollection = initCardCollection(
            this.noteCardCollection,
            this.dom,
            '.note-collection',
            this.noteService,
            {
                entityKey: 'notes',
                fetchData: (params) => this.noteService.getAll(params),
                extraParams: (params) => {
                    params.set('query', this.query);
                    params.set('organization_ids', this.organizationIds);
                    params.set(
                        'fields',
                        'id,title,expired_at,published_at,short_description,color,' +
                            this.organizationService.getAssociationTableFields(),
                    );
                },
            },
            this._eventCardKnot.bind(this),
            { formatPublishedAt: this.formatPublishedAt.bind(this) },
            { sort: { column: 'published_at', order: 'desc' } },
        )!;
    }

    private _eventCardKnot(cardKnot: Knot, note: Objekt): void {
        this._initNoteActivity(cardKnot, note);
        this._initNoteShowButton(cardKnot, note);
        this._initNoteColor(cardKnot, note);

        this.utilService.initOrganizationLink(cardKnot, note);
    }

    private _initNoteActivity(cardKnot: Knot, note: Objekt): void {
        const cardItem = new Query('.card', cardKnot).getKnot();
        const expiredAt = DateIO.parse(note.get<string>('expired_at'));
        const cssClass = DateIO.isBefore(new Date(), expiredAt)
            ? 'active'
            : 'inactive';
        cardItem.addClass(cssClass);
    }

    private _initNoteColor(cardKnot: Knot, note: Objekt): void {
        const color = note.get<string>('color');
        const card = new Query('.card', cardKnot).getKnot();
        const expiredAt = DateIO.parse(note.get<string>('expired_at'));
        if (DateIO.isBefore(new Date(), expiredAt)) {
            card.setStyle({
                'border-left': format('3px solid {0}', [color]),
            });
        }
    }

    formatPublishedAt(publishedAt: string): string {
        return this.languageService.format(
            DateIO.parse(publishedAt),
            'format.datetime',
        );
    }

    private _initNoteShowButton(cardKnot: Knot, note: Objekt): void {
        this.helper.link(
            'a.card',
            cardKnot,
            (href) => {
                this.state.go(href);
            },
            this.state.resolveUrl('notes.show', {
                noteId: note.get<string>('id'),
            }),
        );
    }
}

export const notesIndexController = app.controller(
    'notesIndexController',
    NotesIndexController,
);
